import JSZip from 'jszip';
import { contrastTextColour, lightTint } from 'src/secret-menu/brand-asset-builder/lib/hexColour';
import type { BrandAssetBuilderTableStyle } from 'src/secret-menu/brand-asset-builder/types';

// Helper to create a regex that matches a string even if XML tags are interspersed
function createXmlRegex(str: string) {
  const chars = str.split('');
  const regexStr = chars
    .map(c => c.replace(/([.*+?^=!:${}()|[\]/\\])/g, '\\$1'))
    .join('(?:<[^>]+>)*');
  return new RegExp(regexStr, 'g');
}

// We need to safely find {{CLIENT_NAME}} and {{CURRENT_DATE}} even if they are broken across XML tags.
// Example: <w:t>{</w:t></w:r><w:r><w:t>{CLIENT_NAME</w:t></w:r><w:r><w:t>}}</w:t>
/**
 * @public — ported unchanged from the standalone tool, where this is exported but only ever
 * called internally by `generateDocx` in this same file. Kept exported for parity with the
 * source.
 */
export function safeReplace(xml: string, placeholder: string, replacement: string): string {
  const regex = createXmlRegex(placeholder);
  return xml.replace(regex, match => {
    let isFirstText = true;
    let result = '';
    let inTag = false;
    let currentText = '';

    for (let i = 0; i < match.length; i++) {
      const char = match[i];
      if (char === '<') {
        if (currentText.length > 0) {
          if (isFirstText) {
            result += replacement;
            isFirstText = false;
          }
          currentText = '';
        }
        inTag = true;
        result += char;
      } else if (char === '>') {
        inTag = false;
        result += char;
      } else {
        if (inTag) {
          result += char;
        } else {
          currentText += char;
        }
      }
    }
    if (currentText.length > 0) {
      if (isFirstText) {
        result += replacement;
      }
    }
    return result;
  });
}

// Function to update all image extents in header files using regex to avoid namespace issues
function patchImageExtents(xmlString: string, newRatio: number): string {
  let patched = xmlString;

  // Find all wp:extent and a:ext tags
  const extentRegex = /<wp:extent\s+cx="(\d+)"\s+cy="(\d+)"/g;
  const aExtRegex = /<a:ext\s+cx="(\d+)"\s+cy="(\d+)"/g;

  const replaceFunc = (match: string, cxStr: string, cyStr: string) => {
    const cx = parseInt(cxStr, 10);
    const cy = parseInt(cyStr, 10);
    if (cx > 0 && cy > 0) {
      const origRatio = cx / cy;
      let newCx = cx;
      let newCy = cy;

      if (newRatio > origRatio) {
        // New logo is wider. Keep width (cx), shrink height (cy)
        newCx = cx;
        newCy = Math.round(cx / newRatio);
      } else {
        // New logo is taller. Keep height (cy), shrink width (cx)
        newCy = cy;
        newCx = Math.round(cy * newRatio);
      }
      // Replace only the numbers
      return match
        .replace(`cx="${cxStr}"`, `cx="${newCx}"`)
        .replace(`cy="${cyStr}"`, `cy="${newCy}"`);
    }
    return match;
  };

  patched = patched.replace(extentRegex, replaceFunc);
  patched = patched.replace(aExtRegex, replaceFunc);

  return patched;
}

/**
 * @public — ported unchanged from the standalone tool, where this is exported but only ever
 * called internally by `generateDocx` in this same file. Kept exported for parity with the
 * source.
 */
export function patchColorsInXml(
  xmlString: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  palette: any,
  headingFont: string,
  bodyFont: string,
): string {
  let patched = xmlString;

  const removeHash = (hex: string) => (hex || '').replace('#', '').toUpperCase();

  const primary = removeHash(palette.primary);
  const secondary = removeHash(palette.secondary);
  const accent = removeHash(palette.accent);
  const darkText = removeHash(palette.darkText);

  // Helper to determine text contrast
  const getContrastColor = (hexcolor: string, darkTextHex: string) => {
    if (!hexcolor) return 'FFFFFF';
    const hex = removeHash(hexcolor);
    if (hex.length !== 6) return 'FFFFFF';
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luma >= 140 ? darkTextHex : 'FFFFFF';
  };

  // Helper to create a light tint of a color
  const getLightTint = (hexcolor: string) => {
    if (!hexcolor) return 'F0F0F0';
    const hex = removeHash(hexcolor);
    if (hex.length !== 6) return 'F0F0F0';
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    // Mix 30% color with 70% white to keep it vibrant but light
    const mix = (c: number) =>
      Math.round(c * 0.3 + 255 * 0.7)
        .toString(16)
        .padStart(2, '0');
    return (mix(r) + mix(g) + mix(b)).toUpperCase();
  };

  const lightPrimary = getLightTint(palette.primary);
  const headerTextColor = getContrastColor(lightPrimary, darkText);

  // Original colors found in the master templates
  const colorMap = [
    { orig: '0E2841', new: darkText },
    { orig: '156082', new: lightPrimary }, // Master template blue header
    { orig: 'E97132', new: secondary },
    { orig: '196B24', new: accent },
    { orig: '467886', new: primary },
    { orig: 'BFD95F', new: lightPrimary }, // AfternoonTable green header
    { orig: 'A02B93', new: secondary }, // Pink section line
    { orig: '203B46', new: darkText },
    { orig: 'FF771A', new: lightPrimary }, // TableGrid orange header
    { orig: '4EA72E', new: primary },

    // Additional colors
    { orig: '0F9ED5', new: primary }, // Additional theme blue
    { orig: 'B5055A', new: secondary }, // Additional magenta
  ];

  colorMap.forEach(c => {
    const regex = new RegExp(
      `val="${c.orig}"|fill="${c.orig}"|color="${c.orig}"|hex="${c.orig}"`,
      'gi',
    );
    patched = patched.replace(regex, match => match.replace(new RegExp(c.orig, 'i'), c.new));
    // Also replace raw hex just in case it's in other attributes
    const rawRegex = new RegExp(`"${c.orig}"`, 'gi');
    patched = patched.replace(rawRegex, `"${c.new}"`);
  });

  // Font patching
  if (headingFont) {
    patched = patched.replace(/"Aptos Display"/gi, `"${headingFont}"`);
  }
  if (bodyFont) {
    patched = patched.replace(/"Aptos"/gi, `"${bodyFont}"`);
  }

  // Force table header row text to adapt to primary background color
  patched = patched.replace(
    /(<w:tblStylePr[^>]*w:type="firstRow"[^>]*>.*?<w:rPr>.*?<w:color\s+w:val=")[^"]+(".*?<\/w:rPr>)/g,
    `$1${headerTextColor}$2`,
  );

  return patched;
}

// `AfternoonTable` (banded, coloured borders) and `TableGrid` (Word's built-in plain grid) are
// both already defined in the master templates' styles.xml - this only chooses which one
// `document.xml`'s table points at.
const TABLE_STYLE_ID: Record<BrandAssetBuilderTableStyle, 'AfternoonTable' | 'TableGrid'> = {
  banded: 'AfternoonTable',
  grid: 'TableGrid',
};

// Recolours just the outer border of the active table style (top/left/bottom/right), leaving the
// internal gridlines the neutral grey the template ships with - independent of the header fill,
// which `patchColorsInXml` above already ties to the primary brand colour.
function applyTableBorderColour(
  xml: string,
  styleId: 'AfternoonTable' | 'TableGrid',
  borderColourHex: string,
): string {
  const border = (borderColourHex || '').replace('#', '').toUpperCase();
  if (border.length !== 6) return xml;

  const styleBlockRegex = new RegExp(
    `(<w:style[^>]*w:styleId="${styleId}"[^>]*>[\\s\\S]*?)(<\\/w:style>)`,
  );
  return xml.replace(styleBlockRegex, (match, body: string, close: string) => {
    const newBody = body.replace(
      /<w:tblBorders>([\s\S]*?)<\/w:tblBorders>/,
      (bordersMatch: string, inner: string) => {
        const newInner = inner.replace(
          /(<w:(?:top|left|bottom|right)\b[^>]*\bw:color=")[^"]+(")/g,
          `$1${border}$2`,
        );
        return `<w:tblBorders>${newInner}</w:tblBorders>`;
      },
    );
    return newBody + close;
  });
}

// Overrides the active table style's header fill + text colour, replacing whatever
// `patchColorsInXml` above already set them to (it always ties the header to a light tint of
// primary - this lets the header be an independently-chosen colour instead).
function applyTableHeaderColour(
  xml: string,
  styleId: 'AfternoonTable' | 'TableGrid',
  fillHex: string,
  textHex: string,
): string {
  const fill = (fillHex || '').replace('#', '').toUpperCase();
  const text = (textHex || '').replace('#', '').toUpperCase();
  if (fill.length !== 6 || text.length !== 6) return xml;

  const styleBlockRegex = new RegExp(
    `(<w:style[^>]*w:styleId="${styleId}"[^>]*>[\\s\\S]*?)(<\\/w:style>)`,
  );
  return xml.replace(styleBlockRegex, (match, body: string, close: string) => {
    const newBody = body.replace(
      /<w:tblStylePr[^>]*w:type="firstRow"[^>]*>[\s\S]*?<\/w:tblStylePr>/,
      firstRowMatch => {
        let updated = firstRowMatch.replace(/(<w:shd[^>]*w:fill=")[^"]+(")/, `$1${fill}$2`);
        if (/<w:color[^>]*w:val="[^"]+"[^>]*>/.test(updated)) {
          updated = updated.replace(/(<w:color[^>]*w:val=")[^"]+("[^>]*>)/, `$1${text}$2`);
        } else if (/<w:rPr>/.test(updated)) {
          updated = updated.replace(/<w:rPr>/, `<w:rPr><w:color w:val="${text}"/>`);
        } else if (/<\/w:pPr>/.test(updated)) {
          // Has pPr but no rPr at all (schema order: pPr, then rPr).
          updated = updated.replace(
            /<\/w:pPr>/,
            `</w:pPr><w:rPr><w:color w:val="${text}"/></w:rPr>`,
          );
        } else {
          // No pPr or rPr at all - e.g. TableGrid's original firstRow block, which only has
          // <w:tblPr/><w:tcPr>. Insert rPr right after the opening tag, which is schema-correct
          // whenever there's no pPr to come first.
          updated = updated.replace(
            /^(<w:tblStylePr[^>]*>)/,
            `$1<w:rPr><w:color w:val="${text}"/></w:rPr>`,
          );
        }
        return updated;
      },
    );
    return newBody + close;
  });
}

// Recolours just the header row's cell text in document.xml (the hardcoded-white run colours) to
// match a header fill chosen independently of `patchColorsInXml`'s own contrast computation.
// Scoped to the one row carrying `<w:tblHeader/>` so it can't touch unrelated body text that
// happens to already be the same colour.
function applyTableHeaderRowTextColour(xml: string, textHex: string): string {
  const text = (textHex || '').replace('#', '').toUpperCase();
  if (text.length !== 6) return xml;

  return xml.replace(/<w:tr\b(?:(?!<\/w:tr>)[\s\S])*?<w:tblHeader\/>[\s\S]*?<\/w:tr>/, headerRow =>
    headerRow.replace(/<w:color w:val="[0-9A-Fa-f]{6}"\/>/g, `<w:color w:val="${text}"/>`),
  );
}

// Colours the bullet glyph (not the following text) for every level of the template's one bullet
// list definition. `w:rPr` on a `w:lvl` isn't present in the master templates, so this appends one
// in the schema-correct position (after `w:pPr`, immediately before the level closes).
function applyBulletColour(xml: string, bulletColourHex: string): string {
  const colour = (bulletColourHex || '').replace('#', '').toUpperCase();
  if (colour.length !== 6) return xml;

  return xml.replace(
    /<\/w:pPr>(\s*)<\/w:lvl>/g,
    `</w:pPr>$1<w:rPr><w:color w:val="${colour}"/></w:rPr></w:lvl>`,
  );
}

// Swaps the bullet glyph for every level of the template's one bullet list definition. `glyph` is
// always a plain Unicode punctuation character (see lib/bulletStyles.ts) so it renders correctly
// without also needing a Wingdings/Symbol `w:rFonts` override, unlike Word's own bullet gallery.
function applyBulletGlyph(xml: string, glyph: string): string {
  if (!glyph) return xml;
  const escaped = glyph
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return xml.replace(/<w:lvlText w:val="[^"]*"/g, `<w:lvlText w:val="${escaped}"`);
}

// The master templates' one bullet list (`numId=1`) has no named style pointing at it - nothing
// in Word's UI (Styles pane, Bullets gallery) can select it, so it can only ever be applied by
// editing XML directly. Registering it as a proper `w:type="numbering"` style, the same mechanism
// Word's own built-in list styles use, makes it a real, selectable, named option - "Brand bullet
// list" appears in the Styles pane like any other style, ready for someone to apply to a
// paragraph. `NoList` is Word's own base style for user-defined numbering styles.
function registerBulletNumberingStyle(xml: string, styleId: string, styleName: string): string {
  if (xml.includes(`w:styleId="${styleId}"`)) return xml;
  if (!xml.includes('</w:styles>')) return xml;

  const styleXml =
    `<w:style w:type="numbering" w:styleId="${styleId}">` +
    `<w:name w:val="${styleName}"/>` +
    `<w:basedOn w:val="NoList"/>` +
    `<w:pPr><w:numPr><w:numId w:val="1"/></w:numPr></w:pPr>` +
    `</w:style>`;
  return xml.replace('</w:styles>', `${styleXml}</w:styles>`);
}

// Word's own "List Bullet" through "List Bullet 5" gallery/Styles-pane entries are "latent"
// styles - Word invents its own default definition for each (plain black disc, its own
// numbering) the first time someone applies it, entirely independent of anything in
// numbering.xml, UNLESS the document already defines that styleId explicitly. Pre-defining them
// here, pointing at `numId=1` at the matching indent level (Word's convention: "List Bullet N"
// is level N-1), means clicking any of Word's own bullet styles - not just the custom "Brand
// bullet list" registered above - picks up the chosen colour and shape too.
const BUILTIN_LIST_BULLET_STYLES: { styleId: string; name: string; level: number }[] = [
  { styleId: 'ListBullet', name: 'List Bullet', level: 0 },
  { styleId: 'ListBullet2', name: 'List Bullet 2', level: 1 },
  { styleId: 'ListBullet3', name: 'List Bullet 3', level: 2 },
  { styleId: 'ListBullet4', name: 'List Bullet 4', level: 3 },
  { styleId: 'ListBullet5', name: 'List Bullet 5', level: 4 },
];

function overrideBuiltinListBulletStyles(xml: string): string {
  if (!xml.includes('</w:styles>')) return xml;

  return BUILTIN_LIST_BULLET_STYLES.reduce((current, { styleId, name, level }) => {
    const withoutExisting = current.replace(
      new RegExp(`<w:style[^>]*w:styleId="${styleId}"[^>]*>[\\s\\S]*?<\\/w:style>`),
      '',
    );
    const styleXml =
      `<w:style w:type="paragraph" w:styleId="${styleId}">` +
      `<w:name w:val="${name}"/>` +
      `<w:basedOn w:val="Normal"/>` +
      `<w:uiPriority w:val="99"/>` +
      `<w:unhideWhenUsed/>` +
      `<w:pPr><w:numPr><w:ilvl w:val="${level}"/><w:numId w:val="1"/></w:numPr>` +
      `<w:contextualSpacing/></w:pPr>` +
      `</w:style>`;
    return withoutExisting.replace('</w:styles>', `${styleXml}</w:styles>`);
  }, xml);
}

// The band colours in the master templates' `AfternoonTable` style are a fixed light grey/white -
// this recolours the tinted band to a lighter version of the (already-tinted) header fill, so
// banding always reads as "a paler version of the header" rather than an unrelated fixed grey.
function applyTableBandColour(
  xml: string,
  styleId: 'AfternoonTable' | 'TableGrid',
  bandFillHex: string,
): string {
  const fill = (bandFillHex || '').replace('#', '').toUpperCase();
  if (fill.length !== 6) return xml;

  const styleBlockRegex = new RegExp(
    `(<w:style[^>]*w:styleId="${styleId}"[^>]*>[\\s\\S]*?)(<\\/w:style>)`,
  );
  return xml.replace(styleBlockRegex, (match, body: string, close: string) => {
    const newBody = body.replace(
      /(<w:tblStylePr[^>]*w:type="band1Horz"[^>]*>[\s\S]*?<w:shd[^>]*w:fill=")[^"]+("[^>]*>[\s\S]*?<\/w:tblStylePr>)/,
      `$1${fill}$2`,
    );
    return newBody + close;
  });
}

export async function generateDocx(
  masterBuffer: ArrayBuffer,
  replacements: {
    clientName?: string;
    currentDate?: string;
    image1Buffer?: ArrayBuffer;
    image2Buffer?: ArrayBuffer;
    logoRatio?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    palette?: any;
    headingFont?: string;
    bodyFont?: string;
    tableStyle?: BrandAssetBuilderTableStyle;
    tableBorderColour?: string;
    tableHeaderColour?: string;
    tableHeaderLighten?: boolean;
    bulletColour?: string;
    bulletGlyph?: string;
  },
): Promise<Blob> {
  const zip = new JSZip();
  await zip.loadAsync(masterBuffer);

  // 1. Replace media files
  if (replacements.image1Buffer) {
    zip.file('word/media/image1.png', replacements.image1Buffer);
  }
  if (replacements.image2Buffer) {
    zip.file('word/media/image2.png', replacements.image2Buffer);
  }

  // 2. Helper to patch any XML file
  const processXmlFile = async (filePath: string) => {
    const file = zip.file(filePath);
    if (file) {
      let xml = await file.async('string');

      // Color and Font patching for all XML
      if (replacements.palette) {
        xml = patchColorsInXml(
          xml,
          replacements.palette,
          replacements.headingFont || '',
          replacements.bodyFont || '',
        );

        const removeHash = (hex: string) => (hex || '').replace('#', '').toUpperCase();
        const getContrastColor = (hexcolor: string, darkTextHex: string) => {
          if (!hexcolor) return 'FFFFFF';
          const hex = removeHash(hexcolor);
          if (hex.length !== 6) return 'FFFFFF';
          const r = parseInt(hex.substring(0, 2), 16);
          const g = parseInt(hex.substring(2, 4), 16);
          const b = parseInt(hex.substring(4, 6), 16);
          const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
          return luma >= 140 ? darkTextHex : 'FFFFFF';
        };
        const dtHex = removeHash(replacements.palette.darkText);

        // Fix table styles in styles.xml so any downstream injected tables inherit correct high-contrast header text
        if (filePath === 'word/styles.xml') {
          // 1. Force Title and Heading 1-9 to use the primary brand color
          const pHex = removeHash(replacements.palette.primary);
          const headingRegex =
            /(<w:style[^>]*w:styleId="(?:Title|Heading[1-9])"[^>]*>[\s\S]*?<w:rPr>)(.*?<\/w:style>)/g;
          xml = xml.replace(headingRegex, (match, p1, p2) => {
            if (/<w:color/.test(match)) {
              return match.replace(
                /<w:color[^>]*w:val="[^"]+"[^>]*>/,
                `<w:color w:val="${pHex}"/>`,
              );
            }
            return `${p1}<w:color w:val="${pHex}"/>${p2}`;
          });

          // 2. Fix high-contrast text in table headers
          const blockRegex = /<w:tblStylePr[^>]*w:type="firstRow"[^>]*>([\s\S]*?)<\/w:tblStylePr>/g;
          xml = xml.replace(blockRegex, (match, inner) => {
            const fillMatch = inner.match(/<w:shd[^>]*w:fill="([A-F0-9]{6})"[^>]*>/i);
            if (!fillMatch) return match;

            const fillHex = fillMatch[1];
            const contrast = getContrastColor(fillHex, dtHex);

            let newInner = inner;
            if (/<w:color[^>]*w:val="[A-F0-9]{6}"[^>]*>/.test(newInner)) {
              newInner = newInner.replace(
                /(<w:color[^>]*w:val=")[A-F0-9]{6}("[^>]*>)/g,
                `$1${contrast}$2`,
              );
            } else {
              if (/<w:rPr>/.test(newInner)) {
                newInner = newInner.replace(/<w:rPr>/, `<w:rPr><w:color w:val="${contrast}"/>`);
              } else {
                if (/<w:pPr>[\s\S]*?<\/w:pPr>/.test(newInner)) {
                  newInner = newInner.replace(
                    /(<w:pPr>[\s\S]*?<\/w:pPr>)/,
                    `$1<w:rPr><w:color w:val="${contrast}"/></w:rPr>`,
                  );
                } else {
                  newInner = `<w:rPr><w:color w:val="${contrast}"/></w:rPr>` + newInner;
                }
              }
            }
            return match.replace(inner, newInner);
          });
        }

        // Fix hardcoded white text in document.xml table headers (like the first row of the original export_assets table)
        if (filePath === 'word/document.xml') {
          const headerTextColor = getContrastColor(replacements.palette.primary, dtHex);
          xml = xml.replace(/<w:color w:val="FFFFFF"\/>/g, `<w:color w:val="${headerTextColor}"/>`);

          // Enforce A4 page size instead of US Letter
          // 11906 x 16838 twips = exactly A4
          xml = xml.replace(/<w:pgSz [^>]+>/g, '<w:pgSz w:w="11906" w:h="16838"/>');

          // Enforce A4 background image extents (replace the old oversized 9118470x12890976 EMUs)
          // 7560000 x 10692000 EMUs = exactly A4
          xml = xml.replace(/cx="9118470" cy="12890976"/g, 'cx="7560000" cy="10692000"');

          // Snap background image to top-left of the page instead of negative column/paragraph offsets
          xml = xml.replace(
            /<wp:positionH relativeFrom="[^"]+"><wp:posOffset>[^<]+<\/wp:posOffset><\/wp:positionH>/g,
            '<wp:positionH relativeFrom="page"><wp:posOffset>0</wp:posOffset></wp:positionH>',
          );
          xml = xml.replace(
            /<wp:positionV relativeFrom="[^"]+"><wp:posOffset>[^<]+<\/wp:posOffset><\/wp:positionV>/g,
            '<wp:positionV relativeFrom="page"><wp:posOffset>0</wp:posOffset></wp:positionV>',
          );
        }

        // Force update of TOC and fields when opening the document
        if (filePath === 'word/settings.xml') {
          if (!xml.includes('<w:updateFields')) {
            xml = xml.replace('</w:settings>', '<w:updateFields w:val="true"/></w:settings>');
          }
        }

        const tableStyle = replacements.tableStyle || 'banded';
        // Only the pack's own sample tables follow this reference, so it is presentational: it
        // makes the pack look like what was chosen. Generated reports get their table style from
        // the export pipeline, which is why both styles are branded below.
        if (filePath === 'word/document.xml') {
          xml = xml.replace(
            /<w:tblStyle w:val="AfternoonTable"\/>/g,
            `<w:tblStyle w:val="${TABLE_STYLE_ID[tableStyle]}"/>`,
          );
        }

        // Both brand table styles are patched, not just the selected one. Reports are produced by
        // pandoc using this pack as its `--reference-doc`, which imports style *definitions* only -
        // pandoc builds its own tables, and the export pipeline then stamps `AfternoonTable` onto
        // every one of them (`format_document_tables` in word_utils.py). This pack's own
        // `<w:tblStyle>` reference never travels with them. Patching only the selected style leaves
        // `AfternoonTable` holding the generic `lightPrimary` from the colour map above, so every
        // generated report showed a lightened header and the master template's fixed F2F2F2 bands
        // regardless of what was chosen here.
        if (filePath === 'word/styles.xml' && replacements.tableBorderColour) {
          xml = applyTableBorderColour(xml, 'AfternoonTable', replacements.tableBorderColour);
          xml = applyTableBorderColour(xml, 'TableGrid', replacements.tableBorderColour);
        }

        // The header fill defaults to the raw primary brand colour (not a tint of it) - the
        // lightened look is now opt-in via `tableHeaderLighten`, applied on top of whichever
        // colour `tableHeaderColour` holds.
        const rawHeaderFill = replacements.tableHeaderColour || replacements.palette.primary;
        const effectiveHeaderFill = replacements.tableHeaderLighten
          ? lightTint(rawHeaderFill)
          : rawHeaderFill;
        const effectiveHeaderText = contrastTextColour(
          effectiveHeaderFill,
          replacements.palette.darkText || '#111111',
        );

        if (filePath === 'word/styles.xml' && effectiveHeaderFill) {
          xml = applyTableHeaderColour(
            xml,
            'AfternoonTable',
            effectiveHeaderFill,
            effectiveHeaderText,
          );
          xml = applyTableHeaderColour(xml, 'TableGrid', effectiveHeaderFill, effectiveHeaderText);
        }

        if (filePath === 'word/document.xml' && effectiveHeaderFill) {
          xml = applyTableHeaderRowTextColour(xml, effectiveHeaderText);
        }

        // `grid` means "no row banding", so the band has to be actively cleared rather than just
        // left alone: `AfternoonTable` is the style reports use, and its master-template band is a
        // fixed F2F2F2 that would otherwise show through as banding nobody asked for.
        if (filePath === 'word/styles.xml' && effectiveHeaderFill) {
          xml = applyTableBandColour(
            xml,
            'AfternoonTable',
            tableStyle === 'banded'
              ? lightTint(effectiveHeaderFill)
              : replacements.palette.background || '#FFFFFF',
          );
        }

        if (filePath === 'word/numbering.xml' && replacements.bulletColour) {
          xml = applyBulletColour(xml, replacements.bulletColour);
        }

        if (filePath === 'word/numbering.xml' && replacements.bulletGlyph) {
          xml = applyBulletGlyph(xml, replacements.bulletGlyph);
        }

        if (filePath === 'word/styles.xml') {
          xml = registerBulletNumberingStyle(xml, 'BrandBulletList', 'Brand bullet list');
          xml = overrideBuiltinListBulletStyles(xml);
        }
      }

      // Safe text replacement only on document.xml
      if (filePath === 'word/document.xml') {
        // The user's downstream software requires the exact syntax {{CLIENT_NAME}} and {{CURRENT_DATE}} to remain in the generated asset.
        // We run safeReplace with the placeholder itself to consolidate any fragmented XML tags into a single clean <w:t> tag.
        xml = safeReplace(xml, '{{CLIENT_NAME}}', '{{CLIENT_NAME}}');
        xml = safeReplace(xml, '{{CURRENT_DATE}}', '{{CURRENT_DATE}}');
      }

      // Resize headers
      if (
        (filePath === 'word/header1.xml' || filePath === 'word/header2.xml') &&
        replacements.logoRatio
      ) {
        xml = patchImageExtents(xml, replacements.logoRatio);
      }

      zip.file(filePath, xml);
    }
  };

  // 3. Process all critical XML files
  const filesToProcess = [
    'word/document.xml',
    'word/header1.xml',
    'word/header2.xml',
    'word/footer1.xml',
    'word/footer2.xml',
    'word/theme/theme1.xml',
    'word/styles.xml',
    'word/settings.xml',
    'word/numbering.xml',
  ];

  for (const filePath of filesToProcess) {
    await processXmlFile(filePath);
  }

  return zip.generateAsync({ type: 'blob' });
}
